import { usbKeyErrEn } from './i18n/en_US/exception';
import { usbKeyErrZh } from './i18n/zh_CN/exception';
import { CONSTANTS } from './constants';

let _socket;

let _socketConnected = false;

let isRequesting = false;

/**
 * 向本地的JIT服务发送HTTPS请求
 * @param sendParam 待发送的参数
 * @private
 */
function _sendRequest(sendParam) {
    let init = {
        headers: { 'content-type': 'text/plain;charset=UTF-8' },
        method: 'POST',
        body: JSON.stringify(sendParam),
    };
    return fetch(CONSTANTS.HTTPS_URL, init)
        .catch(() => {
            return Promise.reject(_buildHTTPSErrorResult(CONSTANTS.CONNECT_REFUSED));
        })
        .then(response => _parseResponse(response));
}

/**
 * 处理响应
 * @param response Promise返回值
 * @private
 */
function _parseResponse(response) {
    if (response.status !== CONSTANTS.HTTPS_SUCCESS_CODE) {
        return Promise.reject(_buildHTTPSErrorResult(CONSTANTS.REQ_FAILED));
    }

    return response.text().then(text => _parseResText(text));
}

/**
 * 处理响应体的返回值文本
 * @param text Promise返回值
 * @private
 */
function _parseResText(text) {
    if (!_isJson(text)) {
        return Promise.reject(_buildHTTPSErrorResult(CONSTANTS.RES_INVALID));
    }

    let jitResult = JSON.parse(text);
    if (
        !Object.prototype.hasOwnProperty.call(jitResult, 'error') ||
        !Object.prototype.hasOwnProperty.call(jitResult, 'value')
    ) {
        return Promise.reject(_buildHTTPSErrorResult(CONSTANTS.RES_INVALID));
    }

    if (jitResult.error !== 0) {
        return Promise.reject(_buildHTTPSErrorResult(jitResult.error));
    }

    let result = {
        errorCode: CONSTANTS.HTTPS_SUCCESS_CODE,
        errorMsg: '',
        value: jitResult.value,
    };
    return Promise.resolve(result);
}

/**
 * 通过错误码构建错误信息
 * @param errorCode
 * @private
 */
function _buildHTTPSErrorResult(errorCode) {
    let error = {};
    error.en_US = usbKeyErrEn[errorCode];
    error.zh_CN = usbKeyErrZh[errorCode];
    return {
        errorCode: errorCode,
        errorMsg: JSON.stringify(error),
        value: '',
    };
}

/**
 * 判断一个字符串是否是JSON字符串
 * @param str 待判断的字符串
 * @private
 */
function _isJson(str) {
    let flag = false;
    if (str && typeof str == 'string') {
        try {
            let obj = JSON.parse(str);
            if (obj) {
                flag = true;
            }
        } catch (e) {
            flag = false;
        }
    }
    return flag;
}

/**
 * 判断一个对象是否是数组
 * @param obj 待判断的对象
 * @returns {boolean}
 * @private
 */
function _isArray(obj) {
    return Object.prototype.toString.call(obj) === '[object Array]';
}

/**
 * 注册参数
 * @param driverName USBKey的驱动动态库
 * @param driverBase64 驱动动态库的Base64码
 * @param isRefresh 是否刷新
 * @private
 */
function _registerParam(driverName, driverBase64, isRefresh) {
    let sendParam = {
        name: 'RegisterParam',
        p1: _buildInitXml(driverBase64),
        p2: driverName,
        p3: isRefresh,
    };
    return _sendRequest(sendParam);
}

/**
 * 构建初始化步骤中的XML信息
 * @param driverBase64 驱动动态库的Base64码 USBKey驱动动态库的Base64编码值
 * @private
 */
function _buildInitXml(driverBase64) {
    return `<?xml version="1.0" encoding="utf-8"?><authinfo><liblist><lib type="SKF" version="1.1" dllname="${driverBase64}"><algid val="SHA1" sm2_hashalg="sm3"/></lib></liblist></authinfo>`;
}

/**
 * 获取错误码信息
 * @private
 */
function _getErrorCode() {
    let sendParam = { name: 'GetErrorCode' };
    return _sendRequest(sendParam);
}

/**
 * 结束流程
 * @private
 */
function _finalize() {
    let sendParam = { name: 'Finalize' };
    _sendRequest(sendParam).then();
}

/**
 * 安全的初始化功能，关注多种Key类型，适用于登录业务
 * @param driverBase64 驱动动态库的Base64码
 * @private
 */
function _securityRegister(driverBase64) {
    return _registerParam('', driverBase64, true).then(() => _getErrorCode());
}

/**
 * 安全的初始化功能，只关注一种Key类型，适用于制证业务
 * @param driverName USBKey的驱动动态库
 * @param driverBase64 驱动动态库的Base64码
 * @private
 */
function _securityInitialize(driverName, driverBase64) {
    return _registerParam(driverName, driverBase64, false).then(() => _getErrorCode());
}

/**
 * 获取JIT制证工具版本
 * @private
 */
function _getJITVersion() {
    let sendParam = { name: 'GetVersion' };
    return _sendRequest(sendParam);
}

/**
 * 获取指定厂商的证书列表，一个从初始化到结束的完整流程
 * @param driverBase64 USBKey的驱动动态库
 * @private
 */
function _getVendorCerts(driverBase64) {
    let certParam = {
        certType: 'SC',
        certDN: '',
        certSN: '',
        certEmail: '',
        certIssuerDN: '',
        infoType: -1,
    };
    return _securityRegister(driverBase64)
        .then(() => _refreshCertList())
        .then(() => _getCertList(certParam))
        .finally(() => _finalize());
}

/**
 * 获取指定厂商的证书列表
 * @param certType 证书类型
 * @param certDN 证书的DN
 * @param certSN 证书的SN
 * @param certEmail 证书的Email
 * @param certIssuerDN 证书的issuer DN
 * @param infoType 信息类型
 * @private
 */
function _getCertList({ certType, certDN, certSN, certEmail, certIssuerDN, infoType }) {
    let sendParam = {
        name: 'GetCertLists',
        p1: certType,
        p2: certDN,
        p3: certSN.toUpperCase(),
        p4: certEmail,
        p5: certIssuerDN,
        p6: infoType,
    };
    return _sendRequest(sendParam);
}

/**
 * 刷新证书列表，在安装/删除证书后调用
 * @private
 */
function _refreshCertList() {
    let sendParam = { name: 'RefreshCertList', p1: true };
    return _sendRequest(sendParam);
}

/**
 * 获取证书信息
 * @param certType 证书类型，目前默认传 'SC'
 * @param infoType 信息类型，目前默认传 -1
 * @param oid JIT预留字段，目前默认传 'undefined'
 * @private
 */
function _getCertInfo(certType, infoType, oid) {
    let sendParam = {
        name: 'GetCertInfo',
        p1: certType,
        p2: infoType,
        p3: oid,
    };
    return _sendRequest(sendParam);
}

/**
 * 获取USBKey信息列表
 * @private
 */
function _getDeviceList() {
    let sendParam = { name: 'GetDeviceListEx' };
    return _sendRequest(sendParam);
}

/**
 * 设置选择的USBKey名称，表示后续要操作这一把USBKey
 * @param devName USBKey名称
 * @private
 */
function _setDeviceName(devName) {
    let sendParam = { name: 'SetDeviceName', p1: devName };
    return _sendRequest(sendParam);
}

/**
 * 设置USBKey的PIN码
 * @param pinCode USBKey的PIN码
 * @private
 */
function _assignPINCode(pinCode) {
    let sendParam = { name: 'AssignPinCode', p1: pinCode };
    return _sendRequest(sendParam);
}

/**
 * 验证USBKey的PIN码
 * @param appName USBKey内的应用名称
 * @param pinType 要验证哪一个用户类型的PIN码
 * @param pinCode USBKey的PIN码
 * @private
 */
function _verifyPINCode(appName, pinType, pinCode) {
    let sendParam = {
        name: 'VerifyPin',
        p1: appName,
        p2: pinType,
        p3: pinCode,
    };
    return _sendRequest(sendParam);
}

/**
 * 获取USBKey内的应用名称列表
 * @private
 */
function _getAppList() {
    let sendParam = { name: 'GetApplicationList' };
    return _sendRequest(sendParam);
}

/**
 * 获取容器名
 * @private
 */
function _getContainerName() {
    let sendParam = { name: 'GetContainerName' };
    return _sendRequest(sendParam);
}

/**
 * 设置容器名，表示后续要把证书写入该容器
 * @param containerName
 * @private
 */
function _setContainerName(containerName) {
    let sendParam = { name: 'SetContainerName', p1: containerName };
    return _sendRequest(sendParam);
}

/**
 * 创建P10（CSR）
 * @param keyType 加密算法
 * @param keyLen 加密算法的密钥长度
 * @param appName USBKey内的应用名称
 * @param containerName 容器名
 * @param certCN 证书的CN
 * @private
 */
function _createPKCS10(keyType, keyLen, appName, containerName, certCN) {
    let sendParam = {
        name: 'CreatePKCS10',
        p1: keyType,
        p2: keyLen,
        p3: appName,
        p4: containerName,
        p5: certCN,
    };
    return _sendRequest(sendParam);
}

/**
 * 加载容器
 * @param devName USBKey名称
 * @param appName USBKey内的应用名称
 * @param containerName 容器名
 * @private
 */
function _loadContainer(devName, appName, containerName) {
    let sendParam = {
        name: 'LoadContainer',
        p1: devName,
        p2: appName,
        p3: containerName,
    };
    return _sendRequest(sendParam);
}

/**
 * P1 POP签名
 * @param hashAlgorithm 签名Hash算法：如果是SM2则是SM3，如果是RSA则SHA1
 * @param unsignedData 待签名的数据，POP签名时一般是从认证服务器返回的签名模板
 * @private
 */
function _p1PopSign(hashAlgorithm, unsignedData) {
    let sendParam = {
        name: 'P1SignData',
        p1: hashAlgorithm,
        p2: unsignedData,
    };
    return _sendRequest(sendParam);
}

/**
 * 安装签名证书
 * @param signedP7b 以p7b格式保存的签名证书的Base64编码
 * @private
 */
function _installSignedCert(signedP7b) {
    let sendParam = { name: 'InstallSignCert', p1: signedP7b };
    return _sendRequest(sendParam);
}

/**
 * 安装加密证书
 * @param encryptSessionKey 加密会话秘钥，SM2国密算法时传''
 * @param sessionKeyType 会话秘钥类型，SM2国密算法时传''
 * @param encryptedPrivateKey 证书加密私钥的Base64编码
 * @param encryptedCert 以p7b格式保存的加密证书的Base64编码
 * @private
 */
function _installEncryptedCert(encryptSessionKey, sessionKeyType, encryptedPrivateKey, encryptedCert) {
    let sendParam = {
        name: 'InstallEncryptCert',
        p1: encryptSessionKey,
        p2: sessionKeyType,
        p3: encryptedPrivateKey,
        p4: encryptedCert,
    };
    return _sendRequest(sendParam);
}

/**
 * 删除证书
 * @param certSN 证书的SN
 * @private
 */
function _deleteCert(certSN) {
    let sendParam = { name: 'DeleteCertificate', p1: certSN.toUpperCase() };
    return _sendRequest(sendParam);
}

/**
 * P7签名Ex
 * @param detach JIT预留字段，目前默认传0
 * @param includeCert JIT预留字段，目前默认传1
 * @param certSN 证书的SN
 * @param pinCode USBKey的PIN码
 * @param random 签名随机数
 * @private
 */
function _p7SignEx(detach, includeCert, certSN, pinCode, random) {
    let chooseTypeSetter = { name: 'SetCertChooseType', p1: 1 };
    let pinCodeSetter = { name: 'SetPinCode', p1: pinCode };
    let certSetter = {
        name: 'SetCert',
        p1: 'SC',
        p2: '',
        p3: certSN.toUpperCase(),
        p4: '',
        p5: '',
        p6: '',
    };
    let p7SignSetter = {
        name: 'P7SignStrEx',
        p1: '',
        p2: detach,
        p3: includeCert,
        p4: random,
    };
    let items = [chooseTypeSetter, certSetter, pinCodeSetter, p7SignSetter];
    let sendParam = { name: 'BatchCmd', Items: items };
    return _sendRequest(sendParam);
}

/**
 * 获取证书实体
 * @param certSN 证书的SN
 * @private
 */
function _getCertBySN(certSN) {
    let sendParam = { name: 'GetCertBySN', p1: certSN.toUpperCase() };
    return _sendRequest(sendParam);
}

/**
 * 删除指定容器
 * @param devName USBKey名称
 * @param appName USBKey内的应用名称
 * @param containerName 容器名
 * @private
 */
function _deleteContainerByName(devName, appName, containerName) {
    let sendParam = {
        name: 'DeleteContainerByName',
        p1: devName,
        p2: appName,
        p3: containerName,
    };
    return _sendRequest(sendParam);
}

/**
 * 初始化并打开Socket
 * @param onOpen Socket打开时给客户端通知的回调函数
 * @param onMsg Socket收到消息时给客户端通知的回调函数
 * @param onError Socket错误时给客户端通知的回调函数
 * @param onClose Socket关闭时给客户端通知的回调函数
 * @private
 */
function _initSocket(onOpen, onMsg, onError, onClose) {
    try {
        _socket = new WebSocket(CONSTANTS.SOCKET_URL);
        _socket.addEventListener('open', () => _onSocketOpen(onOpen));
        _socket.addEventListener('message', event => _onSocketMsg(event, onMsg));
        _socket.addEventListener('error', () => _onSocketError(onError));
        _socket.addEventListener('close', event => _onSocketClose(event, onClose));
    } catch (e) {
        _onSocketError(onError);
    }
}

/**
 * 构建Socket成功时给客户端返回的结果信息
 * @param value 返回值
 * @private
 */
function _buildSocketSuccessResult(value) {
    return {
        errorCode: CONSTANTS.SOCKET_SUCCESS_CODE,
        errorMsg: '',
        value: value,
    };
}

/**
 * 构建Socket失败时给客户端返回的结果信息
 * @param errorCode 错误码
 * @private
 */
function _buildSocketErrorResult(errorCode) {
    let error = {};
    error.en_US = usbKeyErrEn[errorCode];
    error.zh_CN = usbKeyErrZh[errorCode];
    return {
        errorCode: errorCode,
        errorMsg: JSON.stringify(error),
        value: '',
    };
}

/**
 * Socket打开时的消息处理事件
 * @param onOpen Socket打开时给客户端通知的回调函数
 * @private
 */
function _onSocketOpen(onOpen) {
    if (_socket && _socket.readyState === WebSocket.OPEN) {
        _socketConnected = true;
        onOpen(_buildSocketSuccessResult(''));
    } else {
        // 打开失败
        onOpen(_buildSocketErrorResult(CONSTANTS.SOCKET_OPEN_FAILED));
    }
}

/**
 * Socket打开时的消息处理事件
 * @param event Socket消息事件
 * @param onMsg Socket收到消息时给客户端通知的回调函数
 * @private
 */
function _onSocketMsg(event, onMsg) {
    // 正常data: "{\"id\":\"KeyChanged\", \"return\":0, \"keyList\":[]}"
    if (!_isJson(event.data)) {
        // 返回值格式异常
        onMsg(_buildSocketErrorResult(CONSTANTS.PARAMS_INVALID));
        return;
    }

    let result = JSON.parse(event.data);
    if (
        !Object.prototype.hasOwnProperty.call(result, CONSTANTS.SOCKET_MSG_ID) ||
        !Object.prototype.hasOwnProperty.call(result, CONSTANTS.SOCKET_MSG_RETURN)
    ) {
        // 返回值格式异常
        onMsg(_buildSocketErrorResult(CONSTANTS.PARAMS_INVALID));
        return;
    }

    if (result.return !== CONSTANTS.SOCKET_SUCCESS_CODE) {
        // 返回值失败，查找对应错误码信息
        onMsg(_buildSocketErrorResult(result.return));
        return;
    }

    _handleReturnData(result, onMsg);
}

/**
 * 处理Socket消息
 * @param result Socket消息
 * @param onMsg Socket收到消息时给客户端通知的回调函数
 * @private
 */
function _handleReturnData(result, onMsg) {
    switch (result.id) {
        case CONSTANTS.SOCKET_SUB_KEY_EVENT:
            let KeyChangedEvent = {
                event: result.id,
                errorCode: result.return,
            };
            onMsg(_buildSocketSuccessResult(JSON.stringify(KeyChangedEvent)));
            break;
        case CONSTANTS.SOCKET_KEY_EVENT:
            if (Object.prototype.hasOwnProperty.call(result, CONSTANTS.SOCKET_KEY_LIST)) {
                let KeyChangedEvent = {
                    event: result.id,
                    keyList: result.keyList,
                };
                onMsg(_buildSocketSuccessResult(JSON.stringify(KeyChangedEvent)));
            }
            break;
        default:
            break;
    }
}

/**
 * Socket错误时的消息处理事件
 * @param onError Socket错误时给客户端通知的回调函数
 * @private
 */
function _onSocketError(onError) {
    // Socket错误
    onError(_buildSocketErrorResult(CONSTANTS.SOCKET_SERVER_ERROR));
}

/**
 * Socket关闭时的消息处理事件
 * @param event Socket消息事件
 * @param onClose Socket关闭时给客户端通知的回调函数
 * @private
 */
function _onSocketClose(event, onClose) {
    onClose(_buildSocketSuccessResult('Socket closed.'));
}

/**
 * 向Socket订阅USBKey插拔事件
 * @param vendorDriverNames 需要订阅插拔事件的USBKey驱动名，以英文分号连接
 * @private
 */
function _subKeyEvent(vendorDriverNames) {
    let initParam = {
        id: 'wsSubKeyEvent',
        dllName: vendorDriverNames,
        deviceSN: '',
    };
    _socket.send(JSON.stringify(initParam));
    return _buildSocketSuccessResult('The command for subscribing to the key event was sent.');
}

/**
 * 关闭Socket连接
 * @private
 */
function _closeSocket() {
    _socket.close();
    _socketConnected = false;
}

/**
 * 已经有正在处理中的请求
 * @private
 */
function _isRequestingError() {
    return Promise.reject(_buildHTTPSErrorResult(CONSTANTS.REQUEST_PROCESSING));
}

let usbKeyDriver = {
    /**
     * 获取驱动版本，这里实际是获取JIT制证工具版本
     */
    getDriverVersion: function () {
        if (isRequesting) {
            return _isRequestingError();
        }
        isRequesting = true;
        // 获取版本
        return _securityInitialize('', '')
            .then(() => _getJITVersion())
            .finally(() => {
                _finalize();
                isRequesting = false;
            });
    },

    /**
     * 获取USB端口上插入的指定厂商的所有Key信息
     * @param driverName USBKey的驱动动态库
     * @param driverBase64 驱动动态库的Base64码
     */
    getDeviceList: function (driverName, driverBase64) {
        if (isRequesting) {
            return _isRequestingError();
        }
        isRequesting = true;
        let deviceList = null;
        return _securityInitialize(driverName, driverBase64)
            .then(() => {
                deviceList = _getDeviceList();
                return deviceList;
            })
            .then(() => {
                _finalize();
            })
            .then(() => {
                return deviceList;
            })
            .finally(() => {
                isRequesting = false;
            });
    },

    /**
     * 获取指定厂商的证书列表
     * @param driverName USBKey的驱动动态库
     * @param driverBase64 驱动动态库的Base64码
     */
    getCertList: function (driverName, driverBase64) {
        if (isRequesting) {
            return _isRequestingError();
        }
        isRequesting = true;
        // 按照JIT的要求，查询证书列表时参数按照预设值传递
        let certParam = {
            certType: 'SC',
            certDN: '',
            certSN: '',
            certEmail: '',
            certIssuerDN: '',
            infoType: -1,
        };
        return _securityRegister(driverBase64)
            .then(() => _refreshCertList())
            .then(() => _getCertList(certParam))
            .finally(() => {
                _finalize();
                isRequesting = false;
            });
    },

    /**
     * 获取多个指定厂商的证书列表
     * @param vendorList 厂商列表数组
     */
    getVendorsCertList: function (vendorList) {
        if (isRequesting) {
            return _isRequestingError();
        }
        isRequesting = true;
        if (!_isArray(vendorList)) {
            return Promise.reject(_buildHTTPSErrorResult(CONSTANTS.PARAMS_INVALID));
        }

        let allCerts = [];
        // 这里串行遍历厂商数组，其中有失败的也继续往下执行，用Promise.all会导致有失败就停止
        for (let i = 0; i < vendorList.length; i++) {
            let vendor = vendorList[i];
            if (Object.prototype.hasOwnProperty.call(vendor, 'driverBase64')) {
                try {
                    console.log('正在遍历: ', vendor);
                    _getVendorCerts(vendor.driverBase64).then(result => {
                        if (_isJson(result.value)) {
                            // 把返回值的value字段转为JSON数组，然后添加至allCerts中
                            let vendorCerts = JSON.parse(result.value);
                            vendorCerts.forEach(cert => allCerts.push(cert));
                        }
                    });
                } catch (e) {
                    console.log('遍历厂商时出错', vendor.driverBase64);
                    console.log(e);
                }
            }
        }

        isRequesting = false;
        let totalResult = {
            errorCode: CONSTANTS.HTTPS_SUCCESS_CODE,
            errorMsg: '',
            value: JSON.stringify(allCerts),
        };
        return Promise.resolve(totalResult);
    },

    /**
     * 校验USBKey的PIN码
     * @param driverName USBKey的驱动动态库
     * @param driverBase64 驱动动态库的Base64码
     * @param devName USBKey的名称，name字段
     * @param pinCode USBKey的PIN码
     */
    verifyPINCode: function (driverName, driverBase64, devName, pinCode) {
        if (isRequesting) {
            return _isRequestingError();
        }
        isRequesting = true;
        return (
            _securityInitialize(driverName, driverBase64)
                .then(() => _setDeviceName(devName))
                .then(() => _getAppList())
                // 提取应用名称
                .then(result => Promise.resolve(result.value))
                .then(appName => _verifyPINCode(appName, 'user', pinCode))
                .finally(() => {
                    _finalize();
                    isRequesting = false;
                })
        );
    },

    /**
     * 产生P10（CSR）
     * @param driverName USBKey的驱动动态库
     * @param driverBase64 驱动动态库的Base64码
     * @param devName USBKey的名称，name字段
     * @param pinCode USBKey的PIN码
     * @param keyType 加密算法
     * @param keyLen 加密算法的密钥长度
     * @param containerName USBKey中存放证书的容器名
     * @param certCN 证书的CN
     */
    createPKCS10: function ({ driverName, driverBase64, devName, pinCode, keyType, keyLen, containerName, certCN }) {
        if (isRequesting) {
            return _isRequestingError();
        }
        isRequesting = true;
        let p10Container = { pkcs10: '', containerName: containerName };
        return (
            _securityInitialize(driverName, driverBase64)
                .then(() => _setDeviceName(devName))
                .then(() => _assignPINCode(pinCode))
                .then(() => _getAppList())
                // 提取应用名称
                .then(result => Promise.resolve(result.value))
                .then(appName => _createPKCS10(keyType, keyLen, appName.split(',')[0], containerName, certCN))
                // 提取并保存P10（CSR）结果
                .then(result => {
                    p10Container.pkcs10 = result.value;
                    return Promise.resolve();
                })
                .then(() => _getContainerName())
                // 提取并保存容器名
                .then(result => {
                    p10Container.containerName = result.value;
                    let createP10Result = {
                        errorCode: CONSTANTS.HTTPS_SUCCESS_CODE,
                        errorMsg: '',
                        value: JSON.stringify(p10Container),
                    };
                    return Promise.resolve(createP10Result);
                })
                .finally(() => {
                    _finalize();
                    isRequesting = false;
                })
        );
    },

    /**
     * POP签名
     * @param driverName USBKey的驱动动态库
     * @param driverBase64 驱动动态库的Base64码
     * @param devName USBKey名称
     * @param containerName 容器名
     * @param pinCode USBKey的PIN码
     * @param hashAlgorithm 签名Hash算法：如果是SM2则是SM3，如果是RSA则SHA1
     * @param unsignedData 待签名的数据，POP签名时一般是从认证服务器返回的签名模板
     */
    popSign: function ({ driverName, driverBase64, devName, containerName, pinCode, hashAlgorithm, unsignedData }) {
        if (isRequesting) {
            return _isRequestingError();
        }
        isRequesting = true;
        return (
            _securityInitialize(driverName, driverBase64)
                .then(() => _setDeviceName(devName))
                .then(() => _getAppList())
                // 提取应用名称
                .then(result => Promise.resolve(result.value))
                .then(appName => _loadContainer(devName, appName, containerName))
                .then(() => _assignPINCode(pinCode))
                .then(() => _p1PopSign(hashAlgorithm, unsignedData))
                .finally(() => {
                    _finalize();
                    isRequesting = false;
                })
        );
    },

    /**
     * 安装签名证书
     * @param driverName USBKey的驱动动态库
     * @param driverBase64 驱动动态库的Base64码
     * @param devName USBKey的名称，name字段
     * @param pinCode USBKey的PIN码
     * @param containerName USBKey中存放证书的容器名，
     * @param signedCert 以p7b格式保存的签名证书的Base64编码
     */
    installSignedCert: function ({ driverName, driverBase64, devName, pinCode, containerName, signedCert }) {
        if (isRequesting) {
            return _isRequestingError();
        }
        isRequesting = true;
        return _securityInitialize(driverName, driverBase64)
            .then(() => _setDeviceName(devName))
            .then(() => _setContainerName(containerName))
            .then(() => _assignPINCode(pinCode))
            .then(() => _installSignedCert(signedCert))
            .finally(() =>
                _refreshCertList().then(() => {
                    _finalize();
                    isRequesting = false;
                })
            );
    },

    /**
     * 安装双证书
     * @param driverName USBKey的驱动动态库
     * @param driverBase64 驱动动态库的Base64码
     * @param devName USBKey的名称，name字段
     * @param pinCode USBKey的PIN码
     * @param containerName USBKey中存放证书的容器名，
     * @param signedCert 以p7b格式保存的签名证书的Base64编码
     * @param signedCertSN 签名证书的SN
     * @param encryptSessionKey 加密会话秘钥，SM2国密算法时传''
     * @param sessionKeyType 会话秘钥类型，SM2国密算法时传''
     * @param encryptPrivateKey 证书加密私钥的Base64编码
     * @param encryptCert 以p7b格式保存的加密证书的Base64编码
     */
    installDoubleCerts: function ({
        driverName,
        driverBase64,
        devName,
        pinCode,
        containerName,
        signedCert,
        signedCertSN,
        encryptSessionKey,
        sessionKeyType,
        encryptPrivateKey,
        encryptCert,
    }) {
        if (isRequesting) {
            return _isRequestingError();
        }
        isRequesting = true;
        return (
            _securityInitialize(driverName, driverBase64)
                .then(() => _setDeviceName(devName))
                .then(() => _setContainerName(containerName))
                .then(() => _assignPINCode(pinCode))
                .then(() => _installSignedCert(signedCert))
                // disable_encrypted_cert 开关可临时规避龙脉ukey无法写入加密证书的问题
                .then(
                    () =>
                        !localStorage.getItem('disable_encrypted_cert') &&
                        _installEncryptedCert(encryptSessionKey, sessionKeyType, encryptPrivateKey, encryptCert)
                )
                // 加密证书如果安装失败，则要删除已安装的签名证书，错误信息仍然返回加密证书的结果
                .then(
                    result => Promise.resolve(result),
                    error => _deleteCert(signedCertSN).then(() => Promise.reject(error))
                )
                .finally(() =>
                    _refreshCertList().then(() => {
                        _finalize();
                        isRequesting = false;
                    })
                )
        );
    },

    /**
     * 删除证书
     * @param driverName USBKey的驱动动态库
     * @param driverBase64 驱动动态库的Base64码
     * @param devName USBKey的名称，name字段
     * @param pinCode USBKey的PIN码
     * @param certSN 证书的SN
     */
    deleteCert: function (driverName, driverBase64, devName, pinCode, certSN) {
        if (isRequesting) {
            return _isRequestingError();
        }
        isRequesting = true;
        return _securityInitialize(driverName, driverBase64)
            .then(() => _setDeviceName(devName))
            .then(() => _assignPINCode(pinCode))
            .then(() => _deleteCert(certSN))
            .finally(() =>
                _refreshCertList().then(() => {
                    _finalize();
                    isRequesting = false;
                })
            );
    },

    /**
     * 获取证书实体
     * @param driverName USBKey的驱动动态库
     * @param driverBase64 驱动动态库的Base64码
     * @param certSN 证书的SN
     */
    getCertBySN: function (driverName, driverBase64, certSN) {
        if (isRequesting) {
            return _isRequestingError();
        }
        isRequesting = true;
        return _securityInitialize(driverName, driverBase64)
            .then(() => _getCertBySN(certSN))
            .finally(() => {
                _finalize();
                isRequesting = false;
            });
    },

    /**
     * 用户登录时的P7签名
     * @param driverName USBKey的驱动动态库
     * @param driverBase64 驱动动态库的Base64码
     * @param pinCode USBKey的PIN码
     * @param certSN 证书的SN
     * @param unsignedData 待签名的数据，一般为认证服务器产生的随机数
     */
    loginP7Sign: function (driverName, driverBase64, pinCode, certSN, unsignedData) {
        if (isRequesting) {
            return _isRequestingError();
        }
        isRequesting = true;
        let signed = { signedData: '', signedCert: '' };
        return (
            _securityRegister(driverBase64)
                .then(() => _p7SignEx(0, 1, certSN, pinCode, unsignedData))
                // 提取并保存签名结果
                .then(result => {
                    signed.signedData = result.value;
                    return Promise.resolve();
                })
                // 查询签名证书，目前参数都是JIT的预设值
                .then(() => _getCertInfo('SC', -1, 'undefined'))
                // 提取并保存容器名
                .then(result => {
                    signed.signedCert = result.value;
                    let signResult = {
                        errorCode: CONSTANTS.HTTPS_SUCCESS_CODE,
                        errorMsg: '',
                        value: JSON.stringify(signed),
                    };
                    return Promise.resolve(signResult);
                })
                .finally(() => {
                    _finalize();
                    isRequesting = false;
                })
        );
    },

    /**
     * 删除指定容器
     * @param driverName USBKey的驱动动态库
     * @param driverBase64 驱动动态库的Base64码
     * @param devName USBKey的名称，name字段
     * @param pinCode USBKey的PIN码
     * @param containerName 容器名
     */
    deleteContainerByName: function (driverName, driverBase64, devName, pinCode, containerName) {
        if (isRequesting) {
            return _isRequestingError();
        }
        isRequesting = true;
        return (
            _securityInitialize(driverName, driverBase64)
                .then(() => _setDeviceName(devName))
                .then(() => _assignPINCode(pinCode))
                .then(() => _getAppList())
                // 提取应用名称
                .then(result => Promise.resolve(result.value.split(',')[0]))
                .then(appName => _deleteContainerByName(devName, appName, containerName))
                .finally(() => {
                    _finalize();
                    isRequesting = false;
                })
        );
    },

    /**
     * 初始化并打开Socket
     * @param onOpen Socket打开时给客户端通知的回调函数，参数为Result对象
     * @param onMsg Socket收到消息时给客户端通知的回调函数，参数为Result对象
     * @param onError Socket错误时给客户端通知的回调函数，参数为Result对象
     * @param onClose Socket关闭时给客户端通知的回调函数，参数为Result对象
     */
    initSocket: function (onOpen, onMsg, onError, onClose) {
        return _initSocket(onOpen, onMsg, onError, onClose);
    },

    /**
     * 向Socket订阅USBKey插拔事件
     * @param vendorDriverNames 需要订阅插拔事件的USBKey驱动名，以英文分号连接
     */
    subKeyEvent: function (vendorDriverNames) {
        return _subKeyEvent(vendorDriverNames);
    },

    /**
     * 关闭Socket连接
     */
    closeSocket: function () {
        _closeSocket();
    },
};

export { usbKeyDriver };



// WEBPACK FOOTER //
// ./public/base/utils/usbKeyDriver.js